"use client";

import { ArrowRight, ChevronRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Hero() {
  const {
    ref: textRef,
    isVisible: textVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: imageRef,
    isVisible: imageVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const breadcrumbs = [{
    label: "Newborn",
    to: "/NewbornParentsPage"
  }, {
    label: "Travel",
    to: "/TravelHygienePage"
  }, {
    label: "Childcare",
    to: "/ChildcareCentersPage"
  }];
  return <section data-section-id="2425" id="bye-hygiene-mist-hero" className="relative w-full bg-background text-foreground overflow-hidden">
      <div className="flex flex-col lg:flex-row min-h-[92vh] lg:h-[92vh]">
        {}
        <div ref={imageRef} className={`relative w-full lg:w-[55%] h-[60vh] lg:h-full overflow-hidden transition-all duration-1000 ease-out ${imageVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="relative w-full h-full lg:rounded-r-none rounded-b-lg lg:rounded-bl-lg lg:rounded-tl-lg overflow-hidden">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1711121/img/family_hygiene_mist.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1711121/img/family_hygiene_mist.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1711121/img/family_hygiene_mist.webp-wvc-sizes" alt="Family gently entering a clean space with B.Y.E. Hygiene Mist, soft natural lighting" loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover" />

            {}
            <div className="absolute inset-0 pointer-events-none" style={{
            background: "linear-gradient(115deg, hsla(158, 15%, 25%, 0.2) 0%, hsla(158, 15%, 25%, 0.05) 45%, hsla(42, 45%, 65%, 0.1) 100%)"
          }} />

            {}
            <div className="absolute top-0 right-0 h-full w-1/3 pointer-events-none" style={{
            background: "linear-gradient(270deg, hsla(42, 45%, 65%, 0.3) 0%, hsla(42, 45%, 65%, 0.08) 50%, transparent 100%)",
            mixBlendMode: "screen"
          }} />

            {}
            <div className="absolute inset-0 pointer-events-none opacity-[0.08] mix-blend-overlay" style={{
            backgroundImage: "radial-gradient(hsla(158, 15%, 25%, 0.4) 1px, transparent 1px)",
            backgroundSize: "3px 3px"
          }} />

            {}
            <div className="absolute bottom-6 left-6 lg:bottom-8 lg:left-8 z-10">
              <p className="font-mono text-[11px] lg:text-xs tracking-[0.22em] uppercase" style={{
              color: "hsl(158, 15%, 25%)"
            }}>
                B.Y.E. · Before You Enter · 2024
              </p>
            </div>
          </div>
        </div>

        {}
        <div aria-hidden="true" className="hidden lg:block absolute top-0 bottom-0 left-[55%] w-px" style={{
        background: "hsl(158, 10%, 85%)"
      }} />

        {}
        <div className="relative w-full lg:w-[45%] flex items-center bg-background">
          <div ref={textRef} className={`w-full px-6 sm:px-10 lg:px-24 py-16 lg:py-0 transition-all duration-1000 ease-out ${textVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            {}
            <nav aria-label="Breadcrumb" className="mb-6 flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
              {breadcrumbs.map((crumb, i) => <div key={crumb.to} className="flex items-center gap-2" data-index={i}>
                  <Link to={crumb.to} className="hover:text-primary transition-colors">
                    {crumb.label}
                  </Link>
                  {i < breadcrumbs.length - 1 && <ChevronRight className="size-3 opacity-40" />}
                </div>)}
            </nav>

            {}
            <p className="font-mono text-xs tracking-[0.32em] uppercase mb-8 lg:mb-12 text-primary">
              Portable Hygiene for Peace of Mind & Protection
            </p>

            {}
            <h1 className="font-default font-medium text-foreground leading-[0.95] text-[3.5rem] sm:text-[4.5rem] lg:text-[6rem] xl:text-[7rem] tracking-tighter">
              <span className="block">Before You</span>
              <span className="block italic font-light" style={{
              color: "hsl(158, 15%, 25%)"
            }}>
                Enter™
              </span>
              <span className="block text-[1.5rem] sm:text-[2rem] lg:text-[2.5rem] tracking-normal mt-4 font-light opacity-90">
                A Gentle Daily Practice with Portable Hygiene Mist
              </span>
            </h1>

            {}
            <p className="font-default text-lg lg:text-xl text-foreground mt-8 lg:mt-10 max-w-md leading-relaxed">
              A soft, welcoming ritual for the modern home. Protect your sanctuary with Before You Enter™—a mindful mist designed for families, travelers, and the safety of your little ones.
            </p>

            {}
            <div className="mt-10 lg:mt-12 flex flex-wrap items-center gap-6 lg:gap-8">
              <Button asChild size="lg" className="rounded-lg px-8 h-12 font-default text-sm tracking-[0.05em] uppercase bg-primary text-white hover:brightness-90 shadow-md">
                <Link to="/ShopPage">Shop B.Y.E.</Link>
              </Button>

              <Link to="/AboutPage" className="font-default text-base text-foreground underline underline-offset-8 decoration-1 hover:decoration-2 transition-all inline-flex items-center gap-2 group">
                Learn the Ritual
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </div>

            {}
            <p className="mt-8 font-default text-sm text-muted-foreground max-w-sm leading-relaxed opacity-80">
              Providing portable and convenient hygiene for families on the move, ensuring your sanctuary stays protected wherever you go.
            </p>

            {}
            <div className="hidden lg:flex absolute bottom-10 right-10 flex-col items-end gap-2">
              <span className="font-mono text-[10px] tracking-[0.3em] uppercase" style={{
              color: "hsl(158, 15%, 25%)"
            }}>
                MIST. ENTER.
              </span>
              <div className="w-px h-10" style={{
              background: "hsl(158, 10%, 85%)"
            }} />
              <span className="font-mono text-[10px] tracking-[0.3em] uppercase" style={{
              color: "hsl(158, 15%, 25%)"
            }}>
                BREATHE. REPEAT.
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>;
}